import type { BindingExpression } from "../../../../util/src/blueprint/types.js";
import { resolveBindingExpression } from "../../../../util/src/blueprint/index.js";
import {
  buildBarLoaderDiskStylesForRender,
  getBarLoaderFootprint,
  isBarLoaderCompactVariableWidth,
  type BarLoaderProps,
  type BarLoaderTargetPatch,
} from "../../../../util/src/layout/bar-loader-config.js";
import {
  type DataBinding,
  type InjectedData,
  resolveBinding,
} from "../layout-types.js";

export type {
  BarLoaderBlockStyle,
  BarLoaderCompactDiskAlign,
  BarLoaderCompactWidthMode,
  BarLoaderDiskDefinition,
  BarLoaderDiskStyleEntry,
  BarLoaderLabelOrientation,
  BarLoaderMode,
  BarLoaderProps,
  BarLoaderTargetPart,
  BarLoaderTargetPatch,
  BarLoaderTargetRef,
  BarLoaderUnit,
  SyncBarLoaderDiskStylesResult,
} from "../../../../util/src/layout/bar-loader-config.js";

export {
  applyBarLoaderTargetPatch,
  BAR_LOADER_CAPACITY_TOTAL_KG,
  BAR_LOADER_CAPACITY_TOTAL_LBS,
  BAR_LOADER_DISKS_BY_UNIT,
  BAR_LOADER_SMALL_MODE_MAX_COLUMNS_KG,
  BAR_LOADER_SMALL_MODE_MAX_COLUMNS_LBS,
  BAR_WEIGHT_BY_UNIT,
  buildBarLoaderDiskStylesForRender,
  calculateLoadedDisks,
  calculateLoadedDisksAtCapacity,
  createInitialBarLoaderProps,
  DEFAULT_BAR_LOADER_DISK_STYLE,
  DEFAULT_BAR_LOADER_LABEL_STYLE,
  DEFAULT_BAR_LOADER_LAYOUT_STYLE,
  DEFAULT_COLLARS_WEIGHT,
  getBarLoaderBlockInsets,
  getBarLoaderCapacityTotalWeight,
  getBarLoaderFootprint,
  getBarLoaderWidthDivisor,
  getBarWeight,
  getCollarsWeight,
  getCompactModeMaxDiskColumns,
  getDefaultDiskStyles,
  getEnabledDisks,
  isBarLoaderCompactVariableWidth,
  syncBarLoaderDiskStylesToNodeBounds,
} from "../../../../util/src/layout/bar-loader-config.js";

export interface ResolveBarLoaderVariableFootprintInput {
  props: BarLoaderProps;
  dataBinding?: DataBinding;
  data: InjectedData;
  bindingPatch?: BindingExpression;
  barLoaderTargetPatches?: BarLoaderTargetPatch[];
  behaviorTotalWeight?: number;
  fallbackWidth: number;
}

export function resolveBarLoaderTotalWeight(input: {
  props: BarLoaderProps;
  dataBinding?: DataBinding;
  data: InjectedData;
  bindingPatch?: BindingExpression;
  behaviorTotalWeight?: number;
}): number {
  const { props, dataBinding, data, bindingPatch, behaviorTotalWeight } = input;
  if (bindingPatch != null) {
    const fromBinding = Number(resolveBindingExpression(bindingPatch, data));
    if (Number.isFinite(fromBinding)) return fromBinding;
  }
  if (behaviorTotalWeight != null && Number.isFinite(behaviorTotalWeight)) {
    return behaviorTotalWeight;
  }
  if (dataBinding) {
    return Number(resolveBinding(dataBinding, data)) || 0;
  }
  if (props.totalWeight != null) {
    return Number(props.totalWeight);
  }
  return 0;
}

/** Footprint width for compact variable-width bar loaders (editor + renderer). */
export function resolveBarLoaderVariableFootprintWidth(
  input: ResolveBarLoaderVariableFootprintInput,
): number {
  if (!isBarLoaderCompactVariableWidth(input.props)) {
    return input.fallbackWidth;
  }
  const totalWeight = resolveBarLoaderTotalWeight(input);
  const diskStyles = buildBarLoaderDiskStylesForRender(
    input.props,
    input.barLoaderTargetPatches,
  );
  return getBarLoaderFootprint({ ...input.props, totalWeight }, diskStyles)
    .width;
}
